/**
 * Image Adjustments Utility
 * Lightroom-style photo adjustments using Canvas and LUT-based processing
 */

// Default adjustment values (all at "neutral" position)
export const DEFAULT_ADJUSTMENTS = {
  // Basic tab - Light
  exposure: 0,      // -3 to +3 stops
  contrast: 0,      // -100 to +100
  highlights: 0,    // -100 to +100
  shadows: 0,       // -100 to +100
  whites: 0,        // -100 to +100
  blacks: 0,        // -100 to +100
  // Basic tab - Presence
  clarity: 0,       // -100 to +100 (local contrast)
  dehaze: 0,        // 0 to 100
  vibrance: 0,      // -100 to +100
  saturation: 0,    // -100 to +100
  // Basic tab - Color
  temperature: 0,   // -100 to +100 (warm/cool)
  tint: 0,          // -100 to +100 (green/magenta)
  // Advanced tab - Sharpening
  sharpening: { amount: 0, radius: 0.5 },
  // Advanced tab - Color Grading
  color_grading: {
    lift: { r: 0, g: 0, b: 0 },
    gamma: { r: 1, g: 1, b: 1 },
    gain: { r: 1, g: 1, b: 1 },
  },
  // Advanced tab - Tone Curve
  tone_curve: null,  // [[input, output], ...] in 0-255, or null for identity
  // Advanced tab - Denoise
  denoise: 0,       // 0-100
};

// Default values for sharpening (for comparison)
const DEFAULT_SHARPENING = { amount: 0, radius: 0.5 };

// Default values for color grading (for comparison)
const DEFAULT_COLOR_GRADING = {
  lift: { r: 0, g: 0, b: 0 },
  gamma: { r: 1, g: 1, b: 1 },
  gain: { r: 1, g: 1, b: 1 },
};

/**
 * Check if adjustments are at default (no processing needed)
 */
export function isDefaultAdjustments(adj) {
  if (!adj) return true;

  // Check basic numeric adjustments
  const basicKeys = ['exposure', 'contrast', 'highlights', 'shadows', 'whites', 'blacks', 'clarity', 'dehaze', 'vibrance', 'saturation', 'temperature', 'tint', 'denoise'];
  const basicDefault = basicKeys.every((key) => (adj[key] ?? 0) === 0);
  if (!basicDefault) return false;

  // Check tone curve
  if (adj.tone_curve != null) return false;

  // Check sharpening
  const sharpening = adj.sharpening || DEFAULT_SHARPENING;
  if (sharpening.amount !== 0) return false;

  // Check color grading
  const cg = adj.color_grading || DEFAULT_COLOR_GRADING;
  const cgDefault = DEFAULT_COLOR_GRADING;

  // Check lift (default is 0 for all)
  if (cg.lift?.r !== cgDefault.lift.r || cg.lift?.g !== cgDefault.lift.g || cg.lift?.b !== cgDefault.lift.b) return false;
  // Check gamma (default is 1 for all)
  if (cg.gamma?.r !== cgDefault.gamma.r || cg.gamma?.g !== cgDefault.gamma.g || cg.gamma?.b !== cgDefault.gamma.b) return false;
  // Check gain (default is 1 for all)
  if (cg.gain?.r !== cgDefault.gain.r || cg.gain?.g !== cgDefault.gain.g || cg.gain?.b !== cgDefault.gain.b) return false;

  return true;
}

/**
 * Build a 256-entry lookup table for fast pixel processing
 * @param {function} fn - Function that maps input (0-255) to output (0-255)
 * @returns {Uint8ClampedArray}
 */
function buildLUT(fn) {
  const lut = new Uint8ClampedArray(256);
  for (let i = 0; i < 256; i++) {
    lut[i] = Math.round(Math.max(0, Math.min(255, fn(i))));
  }
  return lut;
}

/**
 * Apply exposure adjustment (multiplicative)
 * @param {number} stops - Exposure compensation in stops (-3 to +3)
 */
function exposureLUT(stops) {
  const factor = Math.pow(2, stops);
  return buildLUT((v) => v * factor);
}

/**
 * Apply contrast adjustment (S-curve around midpoint)
 * @param {number} amount - Contrast amount (-100 to +100)
 */
function contrastLUT(amount) {
  // Contrast factor: 1.0 at 0, higher for positive, lower for negative
  const factor = 1 + amount / 100;
  return buildLUT((v) => {
    const norm = v / 255;
    const adjusted = (norm - 0.5) * factor + 0.5;
    return adjusted * 255;
  });
}

/**
 * Apply highlights adjustment (affects bright tones)
 * @param {number} amount - Highlights amount (-100 to +100)
 */
function highlightsLUT(amount) {
  const strength = amount / 100;
  return buildLUT((v) => {
    const norm = v / 255;
    // Weight: higher for brighter pixels (above 0.5)
    const weight = Math.max(0, (norm - 0.5) * 2);
    const adjustment = strength * weight * 50; // Max 50 units adjustment
    return v + adjustment;
  });
}

/**
 * Apply shadows adjustment (affects dark tones)
 * @param {number} amount - Shadows amount (-100 to +100)
 */
function shadowsLUT(amount) {
  const strength = amount / 100;
  return buildLUT((v) => {
    const norm = v / 255;
    // Weight: higher for darker pixels (below 0.5)
    const weight = Math.max(0, (0.5 - norm) * 2);
    const adjustment = strength * weight * 50;
    return v + adjustment;
  });
}

/**
 * Apply whites adjustment (affects brightest tones)
 * @param {number} amount - Whites amount (-100 to +100)
 */
function whitesLUT(amount) {
  const strength = amount / 100;
  return buildLUT((v) => {
    const norm = v / 255;
    // Weight: only affects top 25% of tones
    const weight = Math.max(0, (norm - 0.75) * 4);
    const adjustment = strength * weight * 30;
    return v + adjustment;
  });
}

/**
 * Apply blacks adjustment (affects darkest tones)
 * @param {number} amount - Blacks amount (-100 to +100)
 */
function blacksLUT(amount) {
  const strength = amount / 100;
  return buildLUT((v) => {
    const norm = v / 255;
    // Weight: only affects bottom 25% of tones
    const weight = Math.max(0, (0.25 - norm) * 4);
    const adjustment = strength * weight * 30;
    return v + adjustment;
  });
}

/**
 * Combine multiple LUTs into one for efficiency
 * @param {Uint8ClampedArray[]} luts - Array of LUTs to combine
 */
function combineLUTs(...luts) {
  if (luts.length === 0) return null;
  if (luts.length === 1) return luts[0];

  const combined = new Uint8ClampedArray(256);
  for (let i = 0; i < 256; i++) {
    let v = i;
    for (const lut of luts) {
      v = lut[v];
    }
    combined[i] = v;
  }
  return combined;
}

/**
 * Build combined LUT for all tonal adjustments
 */
export function buildTonalLUT(adj) {
  const luts = [];

  if (adj.exposure !== 0) {
    luts.push(exposureLUT(adj.exposure));
  }
  if (adj.contrast !== 0) {
    luts.push(contrastLUT(adj.contrast));
  }
  if (adj.highlights !== 0) {
    luts.push(highlightsLUT(adj.highlights));
  }
  if (adj.shadows !== 0) {
    luts.push(shadowsLUT(adj.shadows));
  }
  if (adj.whites !== 0) {
    luts.push(whitesLUT(adj.whites));
  }
  if (adj.blacks !== 0) {
    luts.push(blacksLUT(adj.blacks));
  }

  return combineLUTs(...luts);
}

/**
 * Apply clarity (local contrast) using unsharp mask on luminance
 * This is a simplified version - real clarity uses edge-aware filtering
 * @param {ImageData} imageData - Source image data
 * @param {number} amount - Clarity amount (-100 to +100)
 * @returns {ImageData} - Modified image data
 */
export function applyClarity(imageData, amount) {
  if (amount === 0) return imageData;

  const { data, width, height } = imageData;
  const strength = amount / 100 * 0.5; // Scale down for subtlety

  // Simple 3x3 sharpening kernel for local contrast
  const output = new Uint8ClampedArray(data);

  for (let y = 1; y < height - 1; y++) {
    for (let x = 1; x < width - 1; x++) {
      const idx = (y * width + x) * 4;

      // Calculate luminance of center and neighbors
      const getL = (dx, dy) => {
        const i = ((y + dy) * width + (x + dx)) * 4;
        return 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
      };

      const centerL = getL(0, 0);
      const avgL = (
        getL(-1, -1) + getL(0, -1) + getL(1, -1) +
        getL(-1, 0) + getL(1, 0) +
        getL(-1, 1) + getL(0, 1) + getL(1, 1)
      ) / 8;

      // Local contrast enhancement
      const diff = centerL - avgL;
      const boost = diff * strength;

      // Apply boost to all channels proportionally
      for (let c = 0; c < 3; c++) {
        output[idx + c] = Math.max(0, Math.min(255, data[idx + c] + boost));
      }
    }
  }

  return new ImageData(output, width, height);
}

/**
 * Apply white balance (temperature and tint)
 * @param {ImageData} imageData - Source image data
 * @param {number} temperature - Temperature (-100 to +100, negative=cool, positive=warm)
 * @param {number} tint - Tint (-100 to +100, negative=green, positive=magenta)
 * @returns {ImageData} - Modified image data
 */
export function applyWhiteBalance(imageData, temperature, tint) {
  if (temperature === 0 && tint === 0) return imageData;

  const { data, width, height } = imageData;
  const output = new Uint8ClampedArray(data);

  // Temperature: adjust red/blue balance
  const tempShift = temperature / 100 * 30; // Max 30 units shift
  // Tint: adjust green/magenta balance
  const tintShift = tint / 100 * 20; // Max 20 units shift

  for (let i = 0; i < data.length; i += 4) {
    // Warm = more red/yellow, Cool = more blue
    output[i] = Math.max(0, Math.min(255, data[i] + tempShift));         // R
    output[i + 1] = Math.max(0, Math.min(255, data[i + 1] - tintShift)); // G
    output[i + 2] = Math.max(0, Math.min(255, data[i + 2] - tempShift)); // B
  }

  return new ImageData(output, width, height);
}

/**
 * Apply all adjustments to an image
 * @param {HTMLCanvasElement} canvas - Target canvas
 * @param {HTMLImageElement} image - Source image
 * @param {object} adjustments - Adjustment values
 */
export function applyAdjustments(canvas, image, adjustments) {
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  const adj = { ...DEFAULT_ADJUSTMENTS, ...adjustments };

  // Set canvas size to match image
  canvas.width = image.naturalWidth || image.width;
  canvas.height = image.naturalHeight || image.height;

  // Draw original image
  ctx.drawImage(image, 0, 0);

  // Skip processing if all adjustments are at default
  if (isDefaultAdjustments(adj)) {
    return;
  }

  // Get image data
  let imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const { data } = imageData;

  // Build combined tonal LUT for efficiency
  const tonalLUT = buildTonalLUT(adj);

  // Apply tonal adjustments via LUT (very fast)
  if (tonalLUT) {
    for (let i = 0; i < data.length; i += 4) {
      data[i] = tonalLUT[data[i]];         // R
      data[i + 1] = tonalLUT[data[i + 1]]; // G
      data[i + 2] = tonalLUT[data[i + 2]]; // B
      // Alpha unchanged
    }
  }

  // Apply white balance (requires per-channel processing)
  if (adj.temperature !== 0 || adj.tint !== 0) {
    imageData = applyWhiteBalance(imageData, adj.temperature, adj.tint);
  }

  // Apply clarity (local contrast - most expensive operation)
  if (adj.clarity !== 0) {
    imageData = applyClarity(imageData, adj.clarity);
  }

  // Put processed image back
  ctx.putImageData(imageData, 0, 0);
}

/**
 * Create a debounced version of applyAdjustments for real-time preview
 * @param {number} delay - Debounce delay in ms
 */
export function createDebouncedApply(delay = 100) {
  let timeoutId = null;
  let pendingArgs = null;

  return function debouncedApply(canvas, image, adjustments) {
    pendingArgs = [canvas, image, adjustments];

    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    timeoutId = setTimeout(() => {
      if (pendingArgs) {
        applyAdjustments(...pendingArgs);
        pendingArgs = null;
      }
      timeoutId = null;
    }, delay);
  };
}
